import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LocationCategory,
  RobotStatus,
  ROBOT_STATUS_LABELS,
  type RobotSummaryDto,
} from '@bhops/shared';
import { robotsApi } from '@/api/robots';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { canCorrectToRetirement } from '@/permissions';

interface Props {
  open: boolean;
  onClose: () => void;
  robot: RobotSummaryDto;
  /**
   * 폐기 취소 동선(plan retire-rollback-sale-kind-2026-09 §1-1) — 되돌릴 상태가
   * 선택된 채 열기. 폐기 예정/완료 로봇의 "폐기 취소" 버튼이 IN_STORAGE 를 넣는다.
   */
  initialStatus?: RobotStatus;
}

const MIN_REASON = 10;

// 폐기 계열 진입 선택지는 canCorrectToRetirement 로 거른다. (권한 통합
// 2026-08-27 이후 이 판정은 운영진 전원 true — 서버의 폐기 전용 403 가드도 그때
// 삭제됐다. 종전 주석 "서버가 403 으로 막는다"는 낡은 사실이라 지웠다.)
const RETIREMENT_STATUSES: readonly RobotStatus[] = [
  RobotStatus.RETIREMENT_PLANNED,
  RobotStatus.RETIRED,
];

/**
 * 라운드15 결정3 — 로봇 '상태 정정'.
 *
 * 일반 편집(RobotEditModal)은 상태머신이 허용하는 전이만 선택지로 보여준다.
 * 이 모달은 반대로 **전체 상태를 전부** 노출한다 — 실물과 어긋난 기록을 고치는
 * 것이 목적이라 전이표를 따를 이유가 없기 때문. 그만큼 오용 여지가 크므로
 * 편집과 시각적으로 분리하고(경고색 버튼 + 경고 배너) 사유를 필수로 받는다.
 */
export function RobotStatusCorrectionModal({ open, onClose, robot, initialStatus }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const userRole = useAuthStore((s) => s.user?.role);
  const allowRetirement = userRole ? canCorrectToRetirement(userRole) : false;

  const [status, setStatus] = useState<RobotStatus>(robot.status);
  const [locationCategory, setLocationCategory] = useState<LocationCategory>(
    robot.locationCategory,
  );
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setStatus(initialStatus ?? robot.status);
      setLocationCategory(robot.locationCategory);
      setReason('');
      setError(null);
    }
  }, [open, robot, initialStatus]);

  const changing = status !== robot.status;
  const hasCustomerDeployment = robot.currentDeployedAt != null;
  const hasNonCustomerDeployment = robot.hasActiveNonCustomerDeployment === true;

  // 전이 규칙을 무시한 전체 목록. 폐기 계열만 권한으로 걸러낸다. 현재 상태가
  // 이미 폐기 계열이면 값이 렌더돼야 하므로 권한과 무관하게 포함(RobotForm 의
  // statusChoices 가 origin.status 를 항상 포함시키는 것과 같은 이유).
  const statusChoices = useMemo<RobotStatus[]>(
    () =>
      Object.values(RobotStatus).filter(
        (s) => allowRetirement || !RETIREMENT_STATUSES.includes(s) || s === robot.status,
      ),
    [allowRetirement, robot.status],
  );

  const correct = useMutation({
    mutationFn: () =>
      robotsApi.correctStatus(robot.id, {
        status,
        locationCategory,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot', robot.id] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['robots-count'] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', robot.id] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('상태가 정정되었습니다. 사유가 이력에 기록되었습니다.', '정정 완료');
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    correct.mutate();
  }

  const reasonTooShort = reason.trim().length < MIN_REASON;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="상태 직접 정정"
      width={480}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="submit"
            form="status-correction-form"
            variant="danger"
            disabled={correct.isPending || reasonTooShort}
          >
            {correct.isPending ? '정정 중…' : '정정 실행'}
          </Button>
        </>
      }
    >
      <form id="status-correction-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <div
          style={{
            padding: 10,
            marginBottom: 14,
            borderRadius: 'var(--r-card)',
            background: 'var(--bg-sunken)',
            border: '1px solid var(--fg-danger)',
            color: 'var(--fg-danger)',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          규칙을 건너뛰고 강제로 지정합니다. 사유가 기록에 남습니다.
          <div className="kuun-caption" style={{ marginTop: 4 }}>
            현재: {ROBOT_STATUS_LABELS[robot.status]}
          </div>
        </div>

        <Field
          label="상태"
          htmlFor="correction-status"
          required
          hint="평소 규칙과 상관없이 모든 상태를 고를 수 있습니다."
        >
          <Select
            id="correction-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as RobotStatus)}
          >
            {/* 선택지는 줄이지 않는다 — 배치가 있는 정당한 정정도 있고, 목록에서
                숨기면 "왜 없지?"가 된다. 대신 아래 경고가 이유와 할 일을 말한다. */}
            {statusChoices.map((s) => (
              <option key={s} value={s}>
                {ROBOT_STATUS_LABELS[s]}
              </option>
            ))}
          </Select>
        </Field>

        {/* 오너 실사고(2026-09-10) — #245 를 다른 지점으로 옮기며 상태만 '운영중'으로
            정정했다. 정정은 배치를 만들지 않으므로 로봇이 "운영중인데 어느 고객사에
            있는지 아무도 모르는" 상태가 되고 고객사 화면에서 사라진다. 서버가 이제
            막지만(배치 없으면 400), **누르기 전에** 여기서 말한다 — 저장만 실패하는
            화면은 만들지 않는다는 이 창의 규칙(위 RETIREMENT_STATUSES 주석)과 같다. */}
        {status === RobotStatus.DEPLOYED && !robot.currentStore && (
          <div data-testid="correction-deployed-warning" style={deployedWarning}>
            이 로봇은 지금 어느 매장에도 배치되어 있지 않습니다. <b>‘운영중’은 배치로만</b> 됩니다.
            상태만 바꾸면 어느 고객사에 있는지 시스템이 알 수 없어 고객사 화면에서 사라집니다. 이
            창을 닫고 로봇 상세의 <b>[배치]</b>를 써 주세요. 배치하면 상태도 함께 바뀝니다. 그
            고객사에 매장이 없으면 ‘매장’ 화면에서 먼저 등록해야 합니다.
          </div>
        )}

        {/* 2026-09-23 — 서버 가드(deployed-invariant)와 **같은 판정**을 누르기 전에 말한다.
            '운영중' 은 고객 매장 배치, '기타' 는 행사·파일럿·인증·테스트·사내 배치가 열려 있어야
            성립한다. 가드는 상태가 바뀔 때만 돌므로 경고도 바꿀 때만 띄운다. 판정 근거는 이
            로봇의 활성 배치를 종류로 가른 사실(currentDeployedAt = 고객 배치 출고일,
            hasActiveNonCustomerDeployment = 고객 아닌 배치 여부)이다. */}
        {changing &&
          status === RobotStatus.DEPLOYED &&
          robot.currentStore &&
          !hasCustomerDeployment &&
          hasNonCustomerDeployment && (
            <div data-testid="correction-deployed-noncustomer-warning" style={deployedWarning}>
              이 로봇은 <b>행사, 파일럿, 인증, 테스트, 사내 배치</b> 중 하나에 걸려 있어 ‘운영중’이
              아니라 <b>‘기타’</b>가 맞습니다. ‘운영중’은 고객사 매장에 배치했을 때만 씁니다.
              고객사로 옮긴 것이라면 이 창을 닫고 로봇 상세에서 <b>[회수]</b>한 뒤 고객사 매장으로{' '}
              <b>[배치]</b>하세요.
            </div>
          )}
        {changing && status === RobotStatus.OTHER && !hasNonCustomerDeployment && (
          <div data-testid="correction-other-warning" style={deployedWarning}>
            {hasCustomerDeployment ? (
              <>
                이 로봇은 <b>고객사 매장에 배치</b>돼 있어 ‘기타’가 아니라 <b>‘운영중’</b>이
                맞습니다. 행사나 인증 때문에 뺀 것이라면 이 창을 닫고 로봇 상세에서 <b>[회수]</b>한
                뒤, 유형을 ‘행사·전시’나 ‘인증·시험’으로 골라 <b>[배치]</b>하세요.
              </>
            ) : (
              <>
                ‘기타’는 <b>배치로만</b> 됩니다. 상태만 바꾸면 로봇이 어디 나가 있는지 시스템이 알
                수 없습니다. 이 창을 닫고 로봇 상세의 <b>[배치]</b>에서 유형을 ‘행사·전시’나
                ‘인증·시험’으로 골라 주세요. 배치하면 상태도 ‘기타’로 함께 바뀝니다.
              </>
            )}
          </div>
        )}

        {/* RobotForm 과 동일한 5개 옵션(AS_CENTER 는 호환용이라 노출 X). */}
        <Field label="위치" htmlFor="correction-location">
          <Select
            id="correction-location"
            value={locationCategory}
            onChange={(e) => setLocationCategory(e.target.value as LocationCategory)}
          >
            <option value={LocationCategory.RESEARCH}>연구소</option>
            <option value={LocationCategory.FACTORY}>공장</option>
            <option value={LocationCategory.WAREHOUSE}>창고</option>
            <option value={LocationCategory.CUSTOMER_SITE}>고객 현장</option>
            <option value={LocationCategory.DISPOSAL}>폐기</option>
          </Select>
        </Field>

        <Field
          label="정정 사유"
          htmlFor="correction-reason"
          required
          hint={`실물과 왜 달랐는지 ${MIN_REASON}자 이상 적어 주세요.`}
        >
          <Textarea
            id="correction-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="예: 연구실에서 실물을 확인했는데 배치 이력 없이 상태만 운영중으로 되어 있었음"
          />
        </Field>
      </form>
    </Modal>
  );
}

/** 배치 없이 '운영중'을 고를 때의 경고 — 위험을 알리는 톤(경고색 테두리). */
const deployedWarning: CSSProperties = {
  border: '1px solid var(--fg-danger)',
  borderRadius: 'var(--r-card)',
  background: 'var(--danger-bg)',
  color: 'var(--fg-default)',
  padding: '10px 12px',
  fontSize: 13,
  lineHeight: 1.65,
  marginBottom: 12,
};
